import { format } from "date-fns";
import { StatusChip } from "./JourneyTableRow";
import {
  JOURNEY_STATUS,
  getStatusChipType,
  getStatusLabel,
  isCompletedStatus,
  isDisagreedStatus,
} from "../../constants/journeyStatus";
import { CloseOutlined } from "@mui/icons-material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { calculateDuration, formatVerificationScore } from "../../utils/common";
import useAuthFetch from "../../hooks/useAuthFetch";
import ConfirmBox from "../ConfirmBox";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import JourneyIdentityLogs from "./JourneyIdentityLogs";
import JourneyIdentityUsage from "./JourneyIdentityUsage";

export default function JourneySidePanel({ row, setOpen }) {
  const customFetch = useAuthFetch();


  const [journeyDetails, setJourneyDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState({
    s3Pdf: false,
    recording: false,
  });
  const [openErr, setOpenErr] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [isResyncing, setIsResyncing] = useState({
    s3Pdf: false,
    udsPdf: false,
    recording: false,
  });
  const [kypResyncing, setKypResyncing] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isUploadedToUDS, setIsUploadedToUDS] = useState(false);
  const userRole = JSON.parse(Cookies.get("userDets") || "{}").role;
  const user = JSON.parse(Cookies.get("userDets") || "{}");
  const [showApproveBtn, setShowApproveBtn] = useState(false)
  const [openApprove, setOpenApprove] = useState(false)
  const btnStatus = useRef(null)
  const [auditHistory, setAuditHistory] = useState([])

  const handleResync = async (docType) => {
    try {
      setIsResyncing((prevState) => ({
        ...prevState,
        [docType]: true,
      }));

      let response;
      let status;
      let message;

      if (docType === "s3Pdf" || docType === "udsPdf") {
        docType === "udsPdf" && setOpenConfirm(false);

        const bodyContent = {
          journeyId: row.journeyId,
          uploadToUDS: docType === "udsPdf" ? true : false,
          uploadToS3: docType === "s3Pdf" ? true : false,
        }

        const method = "POST"

        const data = await customFetch(
          `${process.env.REACT_APP_API_URL}/v1/dashboard/utils/createPDF`,
          method,
          bodyContent
        );

        response = data.response;
        message = data.message;
        status = data.status;


      } else {

        const bodyContent = {
          journeyId: row.journeyId,
          roomId: journeyDetails?.videoSDKRoomId,
          sessionId: journeyDetails?.videoSDKSessionId,
        }

        const method = "POST"

        const data = await customFetch(
          `${process.env.REACT_APP_API_URL}/v1/dashboard/utils/resyncRecording`,
          method,
          bodyContent
        );

        response = data.response;
        message = data.message;
        status = data.status;
      }



      if (status === 200) {
        docType === "udsPdf" && setIsUploadedToUDS(true);
        toast.success(
          `${docType === "recording" ? "Recording" : "PDF"
          } Resynced Successfully`,
          {
            position: "bottom-right",
          }
        );
      } else {
        setOpenErr(true);
        setErrMsg(
          message
            ? message
            : `Something went wrong while resyncing ${docType === "recording" ? "Recording" : "PDF"
            }`
        );
      }
      setIsResyncing((prevState) => ({
        ...prevState,
        [docType]: false,
      }));

      fetchJourney();
    } catch (error) {
      setOpenErr(true);
      setErrMsg(error instanceof Error ? error.message : String(error));
      console.error("Error in fetching presigned:", error);
      setIsResyncing((prevState) => ({
        ...prevState,
        [docType]: false,
      }));
    }
  };
  const handleResyncKyp = async () => {
    try {
      setKypResyncing(true);
      const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/utils/resyncKYP`);
      const bodyContent = {
        journeyId: row.journeyId,
        applicationNo: journeyDetails?.applicationNo,
        customerImage: journeyDetails?.customerImage,
      }
      const method = "POST"

      const { response, status, message } = await customFetch(
        url,
        method,
        bodyContent
      );
      if (status === 200) {
        setKypResyncing(false);
        toast.success(`KYP Resynced Successfully`, {
          position: "bottom-right",
        });
      } else {
        setOpenErr(true);
        setErrMsg(
          message
            ? message
            : `Something went wrong while resyncing KYP`
        );
      }
      setKypResyncing(false);
      fetchJourney();
    } catch (error) {
      setOpenErr(true);
      setErrMsg(error instanceof Error ? error.message : String(error));
      setKypResyncing(false);

    }
  };

  const getUDSStatus = () => {
    if (journeyDetails) {
      const status = journeyDetails?.storageIdentifiers?.some(
        (item) => item.type === "pdf" && item.storageType === "UDS"
      );
      setIsUploadedToUDS(status);
    }
  };

  const [changingStatus, setChangingStatus] = useState(false)

  useEffect(() => {
    getUDSStatus();
  }, [journeyDetails]);

  const journeyStatus = journeyDetails?.journeyStatus;
  const isCompleted = isCompletedStatus(journeyStatus);

  const extraDetails = useMemo(
    () => [
      { label: "VideoSDK RoomID", value: journeyDetails?.videoSDKRoomId },
      { label: "VideoSDK SessionId", value: journeyDetails?.videoSDKSessionId },
      { label: "Language", value: journeyDetails?.language },
      { label: "Application No.", value: journeyDetails?.applicationNo },
      {
        label: "DOB",
        value:
          journeyDetails?.dateOfBirth &&
          format(new Date(journeyDetails?.dateOfBirth), "dd MMM yyyy"),
      },
      { label: "Agent Code", value: journeyDetails?.agentCode },
      {
        label: "Journey Start Time",
        value:
          journeyDetails?.startTime &&
          format(new Date(journeyDetails?.startTime), "dd MMM yyyy, hh:mm a"),
      },
      {
        label: "Journey End Time",
        value:
          journeyDetails?.endTime &&
          format(new Date(journeyDetails?.endTime), "dd MMM yyyy, hh:mm a"),
      },
      {
        label: "Journey Duration",
        value:
          journeyDetails?.startTime && journeyDetails?.endTime
            ? calculateDuration(
              journeyDetails?.startTime,
              journeyDetails?.endTime,
              true
            )
            : journeyDetails?.journeyStatus === JOURNEY_STATUS.FAILED
              ? "-"
              : "Ongoing",
      },
      {
        // Average confidence of this journey's SUCCESSFUL spoof-detection and
        // face-match calls, computed at journey end from its IDENTITY logs.
        // "Unscored" when the journey produced none.
        label: "Verification Score",
        value: formatVerificationScore(journeyDetails?.verificationScore),
      },
      { label: "Browser", value: journeyDetails?.deviceDetails?.browserName },
      { label: "Network", value: journeyDetails?.deviceDetails?.network },
      {
        label: "Location",
        value: `${journeyDetails?.locationDetails?.location} (${journeyDetails?.locationDetails?.latitude},${journeyDetails?.locationDetails?.longitude})`,
      },
      {
        label: "PDF uploaded to UDS",
        value:
          journeyDetails?.storageIdentifiers && isUploadedToUDS ? "Yes" : "No",
      },
      {
        label: "PDF uploaded to S3",
        value:
          journeyDetails?.storageIdentifiers &&
            journeyDetails?.storageIdentifiers.some(
              (item) => item.type === "pdf" && item.storageType === "S3"
            )
            ? "Yes"
            : "No",
        docType: "s3Pdf",
        identifier:
          journeyDetails?.storageIdentifiers &&
          journeyDetails?.storageIdentifiers?.find(
            (item) => item.type === "pdf" && item.storageType === "S3"
          )?.identifier,
      },
      {
        label: "Recording uploaded to S3",
        value:
          journeyDetails?.storageIdentifiers &&
            journeyDetails?.storageIdentifiers.some(
              (item) => item.type === "recording" && item.storageType === "S3"
            )
            ? "Yes"
            : "No",
        docType: "recording",
        identifier:
          journeyDetails?.storageIdentifiers &&
          journeyDetails?.storageIdentifiers?.find(
            (item) => item.type === "recording" && item.storageType === "S3"
          )?.identifier,
      },
    ],
    [journeyDetails, isUploadedToUDS]
  );

  const kypDetails = useMemo(
    () => [
      {
        label: /^g[cb]/i.test(journeyDetails?.applicationNo || '')
          ? "Gift City Status"
          : "Digi Status",
        value: journeyDetails?.digiStatus
      }, { label: "UDS Status", value: journeyDetails?.udsStatus },
      { label: "MCA Status", value: journeyDetails?.mcaStatus },
    ],
    [journeyDetails, isUploadedToUDS]
  );

  const fetchJourney = async () => {
    try {
      setIsLoading(true);
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/v1/dashboard/journey/fetch`
      );
      const method = "POST"
      const bodyContent = {
        journeyId: row.journeyId
      }
      const { response, status, message } = await customFetch(
        url,
        method,
        bodyContent
      );
      console.log("response ,", response);

      if (status !== 200) {
        setOpenErr(true);
        setErrMsg(message);
        console.log("Error in fetching journey", response);
      } else {
        setJourneyDetails(response);
        setIsLoading(false);
      }
    } catch (error) {
      setOpenErr(true);
      setErrMsg(error instanceof Error ? error.message : String(error));
      console.error("Error in fetching journey:", error);
    }
  };

  const fetchPresigned = async (unique_id, docType) => {
    try {
      setIsFetching((prevState) => ({
        ...prevState,
        [docType]: true,
      }));

      const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/utils/getS3ViewURL`);

      const bodyContent = {
        unique_id: unique_id,
        journeyId: row.journeyId,
        isPdf: docType === "s3Pdf" ? true : false,
        status: journeyDetails?.journeyStatus,
      }
      const method = "POST"

      const { response, status, message } = await customFetch(
        url,
        method,
        bodyContent
      );

      if (status !== 200) {
        setOpenErr(true);
        setErrMsg(message);
        console.log("Error in fetching presigned", message);
      } else {
        // const data = await response.text();
        setIsFetching((prevState) => ({
          ...prevState,
          [docType]: false,
        }));
        window.open(response.url, "_blank");
      }
    } catch (error) {
      setOpenErr(true);
      setErrMsg(error instanceof Error ? error.message : String(error));
      console.error("Error in fetching presigned:", error);
    }
  };

  useEffect(() => {
    fetchJourney();
  }, []);

  const changeStatus = async (newStatus) => {
    try {
      setChangingStatus(true)
      const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/application/changeJourneyStatus`);

      const method = "POST"

      const bodyContent = {
        applicationNo: journeyDetails?.applicationNo,
        journeyId: journeyDetails?.journeyId,
        username: user?.userName,
        ntID: user?.ntID,
        status: newStatus
      }
      const { response, status, message } = await customFetch(
        url,
        method,
        bodyContent
      );

      if (status === 200) {
        toast.success(`Journey status change successful!`, {
          position: "bottom-right",
        });
      } else {
        setOpenErr(true);
        setErrMsg(
          message
            ? message
            : "Something went wrong while changing journey status!"
        );
      }
      fetchJourney()
      setChangingStatus(false)
    } catch (e) {
      setChangingStatus(false)
      setOpenErr(true);
      setErrMsg("Something went wrong while changing journey status!");
    }
  }

  const fetchAuditHistory = async () => {
    if (journeyDetails?.applicationNo && journeyDetails?.journeyId) {
      try {
        const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/fetchAuditHistory`);

        const method = "POST"

        const bodyContent = {
          applicationNo: journeyDetails?.applicationNo,
          journeyId: journeyDetails?.journeyId,
        }
        const { response, status, message } = await customFetch(
          url,
          method,
          bodyContent
        );

        if (status === 200) {
          setAuditHistory(response.result)
        }
      }
      catch (e) {
      }
    }
  }

  const checkShowApprove = async () => {
    if (journeyDetails?.applicationNo && journeyDetails?.journeyId) {
      try {

        const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/application/showApproveButton`);

        const method = "POST"

        const bodyContent = {
          applicationNo: journeyDetails?.applicationNo,
          journeyId: journeyDetails?.journeyId,
        }
        const { response, status, message } = await customFetch(
          url,
          method,
          bodyContent
        );

        if (status === 200) {
          setShowApproveBtn(response.showReset)
        }
      } catch (e) {
        setShowApproveBtn(false)
      }
    }
  }

  useEffect(() => {
    if (journeyDetails) {
      fetchAuditHistory()
      if (journeyDetails?.journeyStatus !== JOURNEY_STATUS.APPROVER_PENDING && journeyDetails?.journeyStatus !== JOURNEY_STATUS.FAILED) {
        checkShowApprove()
      }
    }
  }, [journeyDetails])

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen(false);
        }}
        className="absolute top-4 right-2"
      >
        <CloseOutlined className="w-5 h-5 border-gray-400" />
      </button>
      <div className="flex bg-gray-100 flex-row items-center px-3 py-4 border-b border-gray-300 ">
        <p className="text-lg text-black-150 font-semibold  mr-2">
          {row.journeyId}
        </p>
        <StatusChip
          text={getStatusLabel(journeyStatus)}
          type={getStatusChipType(journeyStatus) ?? "warning"}
        />
        {(userRole === "ADMIN" || userRole === "ASSESSOR") && (journeyStatus === JOURNEY_STATUS.APPROVER_PENDING || (showApproveBtn && journeyStatus !== JOURNEY_STATUS.FAILED)) && <div className="flex flex-row"> <button
          onClick={() => {
            btnStatus.current = journeyStatus === JOURNEY_STATUS.APPROVER_PENDING ? "approve" : "adhoc-approve"
            setOpenApprove(true)
          }}
          className="  text-white flex items-center  bg-green-500 ml-2 py-0.5 rounded px-3"
        >
          Approve
        </button>
          {(userRole === "ADMIN" || userRole === "ASSESSOR") && journeyStatus === JOURNEY_STATUS.APPROVER_PENDING && <button
            onClick={() => {
              btnStatus.current = "reject"
              setOpenApprove(true)
            }}
            className="  text-white flex items-center  bg-red-600 ml-2 py-0.5 rounded px-5"
          >
            Reject
          </button>}</div>}
      </div>
      <div className=" pt-4 px-3">
        <div className="grid grid-cols-3 border border-gray-300 ">
          {extraDetails.map(
            ({ label, value, identifier = null, docType = null }, i) => {
              return (
                <div
                  key={i}
                  className={`flex flex-col flex-1 px-3 py-2 border border-gray-300`}
                >
                  <p className="text-sm text-black-150 mb-0">{label}</p>
                  {isLoading ? (
                    <div className="w-full mt-2 ">
                      <div className="h-6 w-3/4  bg-gray-300 rounded relative overflow-hidden animate-pulse" />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center  mt-2 mb-0">
                      <p className=" text-base font-semibold text-black-150 mb-0">
                        {value ? value : "-"}
                      </p>
                      {label === "Recording uploaded to S3" ||
                        label === "PDF uploaded to S3" ? (
                        identifier ? (
                          <p
                            onClick={() => fetchPresigned(identifier, docType)}
                            className={`${isFetching[docType]
                              ? "text-black-150"
                              : "text-blue-500 underline font-semibold cursor-pointer"
                              } ml-3 mb-0 text-base`}
                          >
                            {isFetching[docType]
                              ? `Opening your ${docType === "recording" ? "Recording" : "PDF"
                              }...`
                              : "View"}
                          </p>
                        ) : userRole !== "SUPPORT" &&
                          (label === "Recording uploaded to S3" ||
                            (label === "PDF uploaded to S3" && isCompleted)) ? (
                          <p
                            onClick={() => handleResync(docType)}
                            className={`${(
                              label === "Recording uploaded to S3"
                                ? isResyncing.recording
                                : isResyncing.s3Pdf
                            )
                              ? "text-black-150"
                              : "text-blue-500 underline font-semibold cursor-pointer"
                              } ml-3 mb-0 text-base`}
                          >
                            {(
                              label === "Recording uploaded to S3"
                                ? isResyncing.recording
                                : isResyncing.s3Pdf
                            )
                              ? `Resyncing your ${docType === "recording" ? "Recording" : "PDF"
                              }...`
                              : "Resync"}
                          </p>
                        ) : null
                      ) : null}
                      {label === "PDF uploaded to UDS" &&
                        isCompleted &&
                        userRole !== "SUPPORT" && (
                          <p
                            onClick={() => setOpenConfirm(true)}
                            className={`${isResyncing.udsPdf
                              ? "text-black-150"
                              : "text-blue-500 underline font-semibold cursor-pointer"
                              }  ml-3 mb-0  text-base `}
                          >
                            {isResyncing.udsPdf
                              ? `Resyncing your PDF...`
                              : "Resync"}
                          </p>
                        )}
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
        {isCompleted && (
            <>
              <p className="flex flex-row justify-between text-base border-x-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
                KYP Details
                {userRole !== "SUPPORT" &&
                  (journeyDetails?.digiStatus === "Fail" ||
                    journeyDetails?.udsStatus === "Fail" ||
                    journeyDetails?.mcaStatus === "Fail" ||
                    !journeyDetails?.digiStatus ||
                    !journeyDetails?.udsStatus ||
                    !journeyDetails?.mcaStatus) && (
                    <p
                      onClick={() => handleResyncKyp()}
                      className={`${kypResyncing
                        ? "text-black-150"
                        : "text-blue-500 underline font-semibold cursor-pointer"
                        } ml-3 mb-0 text-base`}
                    >
                      {kypResyncing ? `Resyncing...` : "Resync"}
                    </p>
                  )}
              </p>

              {isLoading ? (
                <div className="w-full py-3 px-3">
                  <div className="h-14 w-full  bg-gray-300 rounded relative overflow-hidden animate-pulse" />
                </div>
              ) : (
                <div className="grid grid-cols-3 border border-gray-300 ">
                  {kypDetails.map(({ label, value }, i) => {
                    return (
                      <div
                        key={i}
                        className={`flex flex-col flex-1 px-3 py-2 border border-gray-300`}
                      >
                        <p className="text-sm text-black-150 mb-0">{label}</p>
                        {isLoading ? (
                          <div className="w-full mt-2 ">
                            <div className="h-6 w-3/4  bg-gray-300 rounded relative overflow-hidden animate-pulse" />
                          </div>
                        ) : (
                          <div className="flex flex-row items-center  mt-2 mb-0">
                            <p className=" text-base font-semibold text-black-150 mb-0">
                              {value ? value : "-"}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

        {isDisagreedStatus(journeyStatus) && (
            <div className="mt-4 border-2 border-gray-300 flex flex-col">
              <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
                User Disagreement Reason
              </p>
              {isLoading ? (
                <div className="w-full py-3 px-3">
                  <div className="h-14 w-full  bg-gray-300 rounded relative overflow-hidden animate-pulse" />
                </div>
              ) : (
                <p className="py-3 px-3 text-base ">
                  {journeyDetails?.disagreeReason ? (
                    journeyDetails.disagreeReason
                  ) : (
                    <div className="flex items-center justify-center">
                      No Data Available
                    </div>
                  )}
                </p>
              )}
            </div>
          )}

        {auditHistory.length > 0 && <div className="mt-4 border-x-2 border-t-2 border-gray-300 flex flex-col">
          <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
            Audit History
          </p>
          {isLoading ? (
            <div className="w-full py-3 px-3">
              <div className="h-14 w-full  bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            </div>
          ) : (
            auditHistory.map((history, index) => (
              <div className="px-3 py-2.5 border-b-2 border-gray-300" key={index}>
                {Object.entries(history).map(([key, value]) => (
                  <p key={key}>
                    <strong>{key}:</strong> {value}
                  </p>
                ))}
              </div>
            ))
          )
          }</div>
        }

        <div className="mt-4 border-2 border-gray-300 flex flex-col">
          <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
            Reference Text
          </p>
          {isLoading ? (
            <div className="w-full py-3 px-3">
              <div className="h-14 w-full  bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            </div>
          ) : (
            <p className="py-3 px-3 text-base ">
              {journeyDetails?.referenceText ? (
                journeyDetails.referenceText
              ) : (
                <div className="flex items-center justify-center">
                  No Data Available
                </div>
              )}
            </p>
          )}
        </div>
        <div className="mt-4 border-2 border-gray-300 flex flex-col">
          <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
            Text Matching
          </p>
          {isLoading ? (
            <div className="w-full py-3 px-3">
              <div className="h-20 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            </div>
          ) : (
            <div className="py-3 px-3">
              {journeyDetails?.textMatchings?.length > 0 ? (
                journeyDetails?.textMatchings.map((match, index) => (
                  <div className="" key={index}>
                    <div className="">
                      <strong>Attempt:</strong> {index + 1}
                    </div>
                    <div className="">
                      <strong>Text:</strong> {match.text}
                    </div>
                    <div>
                      <strong>Accuracy:</strong> {match.accuracy}%
                    </div>
                    <div>
                      <strong>Timestamp:</strong>{" "}
                      {format(
                        new Date(match.timestamp),
                        "dd MMM yyyy, hh:mm a"
                      )}
                    </div>
                    {index < journeyDetails?.textMatchings.length - 1 && (
                      <hr className="mt-2 mb-2 border border-gray-300" />
                    )}
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center">
                  No Data Available
                </div>
              )}
            </div>
          )}
        </div>

        <JourneyIdentityUsage
          usage={journeyDetails?.identityApiUsage}
          isLoading={isLoading}
        />

        <JourneyIdentityLogs journeyId={row.journeyId} />

        <div className="mt-4 mb-4 border-2 border-gray-300 flex flex-col">
          <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
            Journey Screenshots
          </p>
          {isLoading ? (
            <div className="w-full py-3 px-3">
              <div className="h-28 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            </div>
          ) : (
            <div className="py-3 px-3">
              {journeyDetails?.journeyScreenshots?.length > 0 ? (
                journeyDetails?.journeyScreenshots.map(
                  ({ page, screenshot }, i) => (
                    <div className="" key={i}>
                      <div className="mb-2">
                        <strong>Page:</strong> {page}
                      </div>
                      <img
                        style={{
                          height: 500,
                          width: 500,
                          objectFit: "contain",
                        }}
                        src={screenshot}
                        alt="Screenshots"
                      />
                      {i < journeyDetails?.journeyScreenshots.length - 1 && (
                        <hr className="mt-3 mb-3 border border-gray-300" />
                      )}
                    </div>
                  )
                )
              ) : (
                <div className="flex items-center justify-center">
                  No Data Available
                </div>
              )}
            </div>
          )}
        </div>

        {journeyDetails?.customerImage && (
          <div className="mt-4 mb-4 border-2 border-gray-300 flex flex-col">
            <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
              Customer Image
            </p>
            {isLoading ? (
              <div className="w-full py-3 px-3">
                <div className="h-28 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
              </div>
            ) : (
              <div className="py-3 px-3">
                <img
                  style={{ height: 500, width: 500, objectFit: "contain" }}
                  src={journeyDetails?.customerImage}
                  alt="Screenshots"
                />
              </div>
            )}
          </div>
        )}

        {journeyDetails?.journeyQna?.length > 0 && (
          <div className="mt-4 mb-4 border-2 border-gray-300 flex flex-col">
            <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
              Journey QnA
            </p>
            {isLoading ? (
              <div className="w-full py-3 px-3">
                <div className="h-28 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
              </div>
            ) : (
              <div className="py-3 px-3">
                {journeyDetails.journeyQna.map((item, index) => (
                  <div key={item.questionId}>
                    <div className="mb-1">
                      <strong>Q:</strong> {item.question}
                    </div>
                    <div className="mb-2">
                      <strong>A:</strong> {item.answer}
                    </div>
                    {index < journeyDetails.journeyQna.length - 1 && (
                      <hr className="mt-2 mb-2 border border-gray-300" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmBox
        open={openErr}
        setOpen={setOpenErr}
        buttonText={"Close"}
        text={errMsg ? errMsg : "An error occurred"}
      />
      <ConfirmBox
        open={openConfirm}
        setOpen={setOpenConfirm}
        buttonText={"Confirm"}
        buttonHandler={async () => {
          handleResync("udsPdf");
        }}
        extraButtonText={"Cancel"}
        extraButtonHandler={() => {
          setOpenConfirm(false);
        }}
        text={
          isUploadedToUDS
            ? "Document already exists on UDS. Are you sure you want to reupload it?"
            : "Are you sure you want to upload the document to UDS?"
        }
      />
      <ConfirmBox
        open={openApprove}
        setOpen={setOpenApprove}
        buttonText={changingStatus ? "Changing Journey Status..." : "Confirm"}
        buttonHandler={async () => {
          if (!changingStatus) {
            await changeStatus(btnStatus.current)
          }
          setOpenApprove(false)
        }}
        extraButtonText={"Cancel"}
        extraButtonHandler={() => {
          setOpenApprove(false);
        }}
        text={`Are you sure you want to change Journey Status to ${btnStatus.current === "reject" ? "Failed" : "Completed"}?`}
      />
    </div>
  );
}
